/**
 * Clay fiscal calendar for the Hierarchy deal overlay's date presets. The fiscal year starts
 * Feb 1 and is named for the calendar year it ends in (Feb 2026 .. Jan 2027 = FY27), the same
 * convention as Delphi's `quarterOf` (`mini-apps/delphi/counting.ts`); web/app-home cannot import
 * a mini-app, so this is the small pure copy the overlay needs. Ranges are inclusive MONTH
 * indexes (`year * 12 + month - 1`) because deal buckets are keyed by close year + month.
 */

export const FISCAL_PRESETS = ['all', 'this_year', 'last_year', 'this_quarter', 'last_quarter'] as const;
export type FiscalPreset = (typeof FISCAL_PRESETS)[number];

export interface MonthRange {
  from: number;
  to: number;
}

export const monthIndex = (year: number, month: number): number => year * 12 + (month - 1);

/** The calendar year + month of `now` in Eastern time (Clay's finance calendar). */
// Built once: constructing an Intl.DateTimeFormat costs far more than formatting with one.
const EASTERN_YEAR_MONTH = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: 'numeric' });

function easternYearMonth(now: Date): { year: number; month: number } {
  const parts = EASTERN_YEAR_MONTH.formatToParts(now);
  const get = (type: string) => Number(parts.find(p => p.type === type)?.value);
  return { year: get('year'), month: get('month') };
}

/** Month index of the first month (Feb) of the fiscal year containing `now`. */
function fiscalYearStart(now: Date): number {
  const { year, month } = easternYearMonth(now);
  return monthIndex(month >= 2 ? year : year - 1, 2);
}

function quarterStart(now: Date): number {
  const { year, month } = easternYearMonth(now);
  const yearStart = fiscalYearStart(now);
  const fiscalMonth = monthIndex(year, month) - yearStart; // 0 = February
  return yearStart + Math.floor(fiscalMonth / 3) * 3;
}

function computeRange(preset: FiscalPreset, now: Date): MonthRange | null {
  switch (preset) {
    case 'all': return null;
    case 'this_year': { const s = fiscalYearStart(now); return { from: s, to: s + 11 }; }
    case 'last_year': { const s = fiscalYearStart(now) - 12; return { from: s, to: s + 11 }; }
    case 'this_quarter': { const s = quarterStart(now); return { from: s, to: s + 2 }; }
    case 'last_quarter': { const s = quarterStart(now) - 3; return { from: s, to: s + 2 }; }
  }
}

// A range depends only on the preset and the Eastern year-month of `now`, but the deal model asks
// for it once per bucket / deal; resolving it (an Intl formatter each time) per row is wasteful, so
// results are memoized per preset + month (at most 5 presets x the months a session spans).
const rangeCache = new Map<string, MonthRange | null>();

/** Inclusive month range for a preset, or null for "all time". Memoized; treat the result as read-only. */
export function fiscalRange(preset: FiscalPreset, now: Date): MonthRange | null {
  if (preset === 'all') return null;
  const { year, month } = easternYearMonth(now);
  const key = `${preset}:${year}-${month}`;
  if (!rangeCache.has(key)) rangeCache.set(key, computeRange(preset, now));
  return rangeCache.get(key) ?? null;
}

/** A null range matches everything; a bounded range never matches an undated deal. */
export function inFiscalRange(range: MonthRange | null, year: number | null, month: number | null): boolean {
  if (range === null) return true;
  if (year === null || month === null) return false;
  const idx = monthIndex(year, month);
  return idx >= range.from && idx <= range.to;
}

/** "Q3-27" / "FY27" for the period a preset resolves to right now; null for all time. */
export function fiscalPeriodName(preset: FiscalPreset, now: Date): string | null {
  const range = fiscalRange(preset, now);
  if (!range) return null;
  if (preset === 'this_year' || preset === 'last_year') {
    return `FY${String((Math.floor(range.from / 12) + 1) % 100).padStart(2, '0')}`;
  }
  const fyStart = range.from - ((range.from - 1) % 12); // month index of the Feb that opens this quarter's FY
  const q = Math.floor((range.from - fyStart) / 3) + 1;
  return `Q${q}-${String((Math.floor(fyStart / 12) + 1) % 100).padStart(2, '0')}`;
}
